import { useSyncExternalStore } from 'react'

import { en, type StringKey } from './en'

/**
 * Resolving a string, and nothing more.
 *
 * There is one language in the bundle today and a second one coming, so the
 * catalogue is looked up through a function from the first commit rather than
 * retrofitted later: the retrofit is what leaves forty strings behind.
 *
 * A missing key renders as the key itself rather than as an empty screen. A
 * blank label is a bug somebody ships; `deficiency.field.title` on the screen
 * is a bug somebody fixes.
 */
export type Catalogue = Partial<Record<StringKey, string>>

/**
 * The store is keyed loosely because half of it does not come from this
 * repository. The instance's own catalogue — every module's message keys, the
 * ones a court entry, an event summary and a refusal are written in — is
 * fetched at sign-in and merged in here, and its keys are whatever the modules
 * loaded on that box declare. `t` stays typed to this build's own strings;
 * `tOr` is the door the server's keys come through.
 */
const catalogues: Record<string, Record<string, string>> = { en: { ...en } }

let language = 'en'

/**
 * Who to tell when the wording changes.
 *
 * The store is plain module state, so its changing re-renders nothing by
 * itself. A screen that drew a server key before the catalogue answered went
 * on showing the key after the sentence had arrived. `useWording` is how a
 * component asks to be drawn again when that happens; nothing is remounted, so
 * nothing anybody had typed is lost.
 */
let revision = 0
const listeners = new Set<() => void>()

function changed(): void {
  revision += 1
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Re-render the calling component whenever a catalogue or the language changes. */
export function useWording(): number {
  return useSyncExternalStore(subscribe, () => revision)
}

export function setLanguage(lang: string): void {
  if (lang === language) return
  language = lang
  changed()
}

export function currentLanguage(): string {
  return language
}

/**
 * Register a catalogue: the instance's own message keys at sign-in, Spanish
 * next, and QAQC's and LOTO's after that.
 *
 * This build's strings win over the server's on a collision. A screen label
 * belongs to the screen, and a module that happens to publish the same key
 * should not be able to reword somebody else's button.
 */
export function addCatalogue(lang: string, catalogue: Record<string, string>): void {
  const own: Record<string, string> = lang === 'en' ? { ...en } : {}
  catalogues[lang] = { ...catalogue, ...catalogues[lang], ...own }
  changed()
}

export function t(key: StringKey, params?: Record<string, string | number>): string {
  const template = catalogues[language]?.[key] ?? catalogues.en?.[key] ?? key
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  )
}

/**
 * A count and its noun, in the one form English needs.
 *
 * `<key>_one` is used when there is exactly one and `<key>` otherwise, so "1
 * RFIs" never reaches a screen. English needs two forms; a language that needs
 * more gets them by adding keys here rather than by every caller learning the
 * rule.
 */
export function tCount(key: StringKey, count: number): string {
  const singular = `${key}_one` as StringKey
  if (count === 1 && catalogues[language]?.[singular] !== undefined) {
    return t(singular, { count: String(count) })
  }
  if (count === 1 && catalogues.en?.[singular] !== undefined) {
    return t(singular, { count: String(count) })
  }
  return t(key, { count: String(count) })
}

/**
 * A key the client does not know — a state the API added, a module code this
 * build has never heard of. Rendered as the raw value rather than hidden, so a
 * new state shows up as `ready_for_reinspection` rather than as a blank cell.
 */
export function tOr(
  key: string,
  fallback: string,
  params?: Record<string, string | number>,
): string {
  const template = catalogues[language]?.[key as StringKey] ?? catalogues.en?.[key as StringKey]
  if (template === undefined) return fallback
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  )
}
